/**
 * GauShala Home Dashboard - Refactored Version
 * Streamlined dashboard with unified API endpoint
 * Displays key metrics and 4 essential charts
 * Global date filter ensures all sections reflect selected time period
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, DollarSign, TrendingUp, Award, AlertCircle, Loader2, Home, Milk, Heart, Calendar, Utensils, Users, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { gauShalaApi, type DashboardSummary, type InventoryStockData, type MedicineStockData, type ShedOccupancyData, type CowDungTransactionData, type MilkProductionData, type CattleStatistics, type UpcomingHealthCheck, type FoodConsumptionSummary } from '../../services/gaushala/api';
import {
  MedicineInventoryChart,
  type MedicineInventoryData,
  InventoryChart,
  type InventoryData,
  ShedManagementChart,
  type ShedData,
  QualityGradeDistributionChart,
  MilkProductionChart,
  type MilkProductionData as ChartMilkProductionData,
} from './components';
import CowDungTransactionChart from './components/CowDungTransactionChart';
import DateRangeFilter, { type DateRange } from './components/DateRangeFilter';

/**
 * Helper Functions for Date Management
 */

/**
 * Get today's date in YYYY-MM-DD format
 */
function getTodayDate(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get the start of current month in YYYY-MM-DD format
 */
function getMonthStartDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
}

/**
 * Format date string from YYYY-MM-DD to user-friendly format (e.g., "Jan 12, 2025")
 */
function formatDateForDisplay(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Get period-specific label suffix for metric card subtitles
 * Used to make labels contextual to selected date range
 * Handles both predefined ranges and custom date ranges
 */
function getPeriodLabelSuffix(dateRangeLabel: string | undefined): string {
  if (!dateRangeLabel) return "selected period";

  // Map for predefined ranges
  const labelMap: Record<string, string> = {
    "Today": "today",
    "This Week": "this week",
    "This Month": "this month",
    "Last 3 Months": "last 3 months",
  };

  // Check if it's a predefined label
  if (labelMap[dateRangeLabel]) {
    return labelMap[dateRangeLabel];
  }

  // For custom ranges (format: "Jan 1, 2025 - Jan 12, 2025"), just use "selected period"
  // because the label itself contains the date info
  return "selected period";
}

/**
 * Metric Card Component - Displays individual metric
 */
function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
}: {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-gray-600 text-sm font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          <p className="text-gray-500 text-xs mt-2">{subtitle}</p>
        </div>
        <Icon className="w-8 h-8 text-blue-500 opacity-70" />
      </div>
    </div>
  );
}

/**
 * Loading Spinner Component
 */
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Loading dashboard data...</p>
      </div>
    </div>
  );
}

/**
 * Error Message Component
 */
function ErrorMessage({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
        <div className="flex items-center mb-4">
          <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
          <h2 className="text-xl font-semibold text-gray-900">Error Loading Dashboard</h2>
        </div>
        <p className="text-gray-600 mb-6">{message}</p>
        <button
          onClick={onRetry}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}

/**
 * Transform cow dung transaction data from API format to chart format
 * API sends: { date, weightKg, qualityGrade, totalAmount, paymentStatus, transactionCount }
 * Chart expects: { date, weightKg, totalAmount }
 */
function transformCowDungData(apiData: CowDungTransactionData[]): typeof apiData {
  if (!apiData || apiData.length === 0) {
    return [];
  }

  return apiData.map(item => ({
    date: item.date,
    weightKg: item.weightKg,
    qualityGrade: item.qualityGrade,
    totalAmount: item.totalAmount,
    paymentStatus: item.paymentStatus,
    transactionCount: item.transactionCount,
  }));
}

/**
 * Transform inventory data from API format to chart format
 * API sends: { category, itemCount, lowStockItems, status, totalValue }
 * Chart expects: { name, quantity, maxQuantity }
 */
function transformInventoryData(apiData: InventoryStockData[]): InventoryData[] {
  if (!apiData || apiData.length === 0) {
    return [];
  }

  return apiData.map(item => ({
    name: item.category,
    quantity: item.itemCount - item.lowStockItems, // Good stock items
    maxQuantity: item.itemCount, // Total items in category
    type: item.status,
  }));
}

/**
 * Transform medicine data from API format to chart format
 * API sends: { medicineName, currentStock, minimumStock, expiryDate, daysToExpiry, status }
 * Chart expects: { name, quantity, maxQuantity }
 * Using minimumStock as maxQuantity for stock level percentage calculation
 */
function transformMedicineData(apiData: MedicineStockData[]): MedicineInventoryData[] {
  if (!apiData || apiData.length === 0) {
    return [];
  }

  return apiData
    .filter(item => item.medicineName) // Filter out incomplete records with null names
    .map(item => ({
      name: item.medicineName,
      quantity: item.currentStock,
      maxQuantity: Math.max(item.minimumStock, 1), // Use minimum stock as baseline, avoid division by zero
    }));
}

/**
 * Transform shed occupancy data from API format to chart format
 * API sends: { shedName, shedCode, capacity, occupancy, occupancyPercentage, status }
 * Chart expects: { shedNumber, capacity, occupancy }
 */
function transformShedData(apiData: ShedOccupancyData[]): ShedData[] {
  if (!apiData || apiData.length === 0) {
    return [];
  }

  return apiData.map(item => ({
    shedNumber: item.shedName || item.shedCode,
    capacity: item.capacity,
    occupancy: item.occupancy,
  }));
}

/**
 * Transform milk production data from API format to chart format
 * API sends: { date, quantity, fatPercentage, snf, recordCount }
 * Chart expects: { date, liters, quality? }
 */
function transformMilkProductionData(apiData: MilkProductionData[]): ChartMilkProductionData[] {
  if (!apiData || apiData.length === 0) {
    return [];
  }

  return apiData.map(item => {
    // Map fat percentage to quality grade
    let quality: 'excellent' | 'good' | 'average' | 'poor' | undefined;
    if (item.fatPercentage >= 4.5) {
      quality = 'excellent';
    } else if (item.fatPercentage >= 4.0) {
      quality = 'good';
    } else if (item.fatPercentage >= 3.5) {
      quality = 'average';
    } else if (item.fatPercentage > 0) {
      quality = 'poor';
    }

    return {
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      liters: item.quantity || 0,
      quality,
    };
  });
}

/**
 * Main Dashboard Component
 */
export default function GauShalaHome() {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize with "This Month" as the default date range for better data visibility
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: getMonthStartDate(),
    endDate: getTodayDate(),
    label: "This Month"
  });

  // Fetch dashboard data on component mount with default date range
  useEffect(() => {
    console.log('📊 Dashboard mounted with default date range:', {
      label: selectedDateRange.label,
      startDate: selectedDateRange.startDate,
      endDate: selectedDateRange.endDate,
    });
    fetchDashboardData(selectedDateRange);
  }, []);

  /**
   * Fetch all dashboard data from unified API endpoint
   * Optionally accepts date range parameters
   * @param isManualRefresh - true when triggered by the Refresh button (shows toast feedback)
   */
  const fetchDashboardData = async (dateRange?: DateRange, isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      console.log('📊 Fetching dashboard data...', dateRange, 'manualRefresh:', isManualRefresh);

      // Single API call to fetch all dashboard data with optional date range
      const data = await gauShalaApi.dashboard.getSummary(
        dateRange?.startDate,
        dateRange?.endDate
      );
      setDashboardData(data);

      console.log('✅ Dashboard data loaded:', data);

      if (isManualRefresh) {
        toast.success('Dashboard refreshed successfully', { duration: 3000 });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard';
      setError(errorMessage);
      console.error('❌ Error fetching dashboard:', err);

      if (isManualRefresh) {
        toast.error('Failed to refresh dashboard. Please try again.', { duration: 4000 });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  /**
   * Handle manual refresh button click - shows toast feedback
   */
  const handleRefresh = () => {
    fetchDashboardData(selectedDateRange, true);
  };

  /**
   * Handle date range filter change
   */
  const handleDateRangeChange = (dateRange: DateRange) => {
    console.log('🔄 Date range changed:', {
      label: dateRange.label,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      labelSuffix: getPeriodLabelSuffix(dateRange.label),
    });
    setSelectedDateRange(dateRange);
    fetchDashboardData(dateRange);
  };

  // Handle loading state
  if (loading) {
    return <LoadingSpinner />;
  }

  // Handle error state
  if (error) {
    return <ErrorMessage message={error} onRetry={fetchDashboardData} />;
  }

  // Handle no data
  if (!dashboardData) {
    return <ErrorMessage message="No dashboard data available" onRetry={fetchDashboardData} />;
  }

  const { metrics, cowDungTransactionData, shedData, inventoryData, medicineData, qualityGradeDistribution, todayCollectionSummary, milkProductionSummary, milkProductionData, cattleStatistics, upcomingHealthChecks, foodConsumptionSummary } = dashboardData;

  // Debug logging for all data including metrics
  console.log('🔍 Dashboard Data Received:', {
    selectedDateRange: {
      label: selectedDateRange?.label,
      startDate: selectedDateRange?.startDate,
      endDate: selectedDateRange?.endDate,
    },
    metrics: metrics,
    todayCollectionSummary: todayCollectionSummary,
    hasQualityGradeDistribution: !!qualityGradeDistribution,
    qualityGradeDistributionLength: qualityGradeDistribution?.length || 0,
    hasTodayCollectionSummary: !!todayCollectionSummary,
  });

  // Transform data from API format to chart format
  const transformedCowDungData = transformCowDungData(cowDungTransactionData);
  const transformedInventoryData = transformInventoryData(inventoryData);
  const transformedMedicineData = transformMedicineData(medicineData);
  const transformedShedData = transformShedData(shedData);
  const transformedMilkProductionData = transformMilkProductionData(milkProductionData || []);

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Loading Overlay - Shows when date filter changes */}
      {loading && dashboardData && (
        <div className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm">
            <div className="flex items-center justify-center gap-3">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              <p className="text-gray-700">Updating dashboard for <strong>{selectedDateRange?.label}</strong>...</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <div className="flex items-center gap-3">
              <DateRangeFilter
                onDateRangeChange={handleDateRangeChange}
                selectedLabel={selectedDateRange?.label || "Today"}
              />
              <button
                onClick={handleRefresh}
                className="inline-flex items-center gap-1.5 text-gray-600 hover:text-gray-900 text-sm font-medium px-3 py-2 rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh dashboard data"
                disabled={loading || refreshing}
                aria-label="Refresh dashboard"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8 ${loading && dashboardData ? 'opacity-50 pointer-events-none' : ''}`}>
        {/* Period Indicator Badge */}
        {selectedDateRange && (
          <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2 mb-4">
            <p className="text-sm text-blue-700">
              📅 Viewing: <strong>{selectedDateRange.label}</strong>
              {" "}
              {selectedDateRange.startDate === selectedDateRange.endDate ? (
                `(${formatDateForDisplay(selectedDateRange.startDate)})`
              ) : (
                `(${formatDateForDisplay(selectedDateRange.startDate)} - ${formatDateForDisplay(selectedDateRange.endDate)})`
              )}
            </p>
          </div>
        )}

        {/* Daily Metrics Section - 6 Cards */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Key Metrics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* Total Cattle Card */}
            <MetricCard
              title="Total Cattle"
              value={cattleStatistics?.totalCattle || metrics?.totalCattle || 0}
              subtitle={`Registered cattle in gaushala`}
              icon={Users}
            />

            {/* Healthy Cattle Card */}
            <MetricCard
              title="Healthy Cattle"
              value={cattleStatistics?.healthyCattle || 0}
              subtitle={`Cattle in good health`}
              icon={Heart}
            />

            {/* Shed Occupancy Card */}
            <MetricCard
              title="Current Occupancy"
              value={metrics?.currentShedOccupancy || 0}
              subtitle={`Cattle currently in sheds`}
              icon={Home}
            />

            {/* Shed Utilization Card */}
            <MetricCard
              title="Shed Utilization"
              value={`${(metrics?.shedUtilizationPercentage || 0).toFixed(1)}%`}
              subtitle={`${metrics?.currentShedOccupancy || 0}/${metrics?.totalShedCapacity || 0} capacity`}
              icon={TrendingUp}
            />

            {/* Milk Production Card */}
            <MetricCard
              title="Milk Production"
              value={`${(milkProductionSummary?.totalMilkProduced || 0).toFixed(1)} L`}
              subtitle={`Total ${getPeriodLabelSuffix(selectedDateRange?.label)}`}
              icon={Milk}
            />

            {/* Average Fat % Card */}
            <MetricCard
              title="Average Fat %"
              value={`${(milkProductionSummary?.averageFatPercentage || 0).toFixed(1)}%`}
              subtitle={`Milk fat content ${getPeriodLabelSuffix(selectedDateRange?.label)}`}
              icon={Milk}
            />

            {/* Collection Weight Card */}
            <MetricCard
              title="Dung Collected"
              value={`${Math.round(todayCollectionSummary?.totalWeight || 0)} KG`}
              subtitle={`Cow dung ${getPeriodLabelSuffix(selectedDateRange?.label)}`}
              icon={Leaf}
            />

            {/* Collection Value Card */}
            <MetricCard
              title="Collection Value"
              value={`₹${Math.round(todayCollectionSummary?.totalValue || 0)}`}
              subtitle={`Revenue ${getPeriodLabelSuffix(selectedDateRange?.label)}`}
              icon={DollarSign}
            />
          </div>
        </section>

        {/* Cattle Health & Upcoming Checks Section */}
        {(cattleStatistics || (upcomingHealthChecks && upcomingHealthChecks.length > 0)) && (
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Cattle Health Breakdown */}
            {cattleStatistics && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  Cattle Health Status
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{cattleStatistics.healthyCattle}</p>
                    <p className="text-sm text-gray-600">Healthy</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-red-600">{cattleStatistics.sickCattle}</p>
                    <p className="text-sm text-gray-600">Sick</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-yellow-600">{cattleStatistics.recoveringCattle}</p>
                    <p className="text-sm text-gray-600">Recovering</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{cattleStatistics.vaccinationDueCattle}</p>
                    <p className="text-sm text-gray-600">Vaccination Due</p>
                  </div>
                </div>

                {/* Breed Distribution */}
                {cattleStatistics.breedDistribution && cattleStatistics.breedDistribution.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Breed Distribution</h4>
                    <div className="space-y-2">
                      {cattleStatistics.breedDistribution.slice(0, 5).map((breed, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">{breed.breedName}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full"
                                style={{ width: `${breed.percentage}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-gray-700 w-12 text-right">{breed.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Upcoming Health Checks */}
            {upcomingHealthChecks && upcomingHealthChecks.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  Upcoming Health Checks
                </h3>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {upcomingHealthChecks.map((check, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        check.status === 'OVERDUE' ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
                      }`}
                    >
                      <div>
                        <p className="font-medium text-gray-900">{check.cattleName}</p>
                        <p className="text-sm text-gray-600">{check.recordType}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-medium ${
                          check.status === 'OVERDUE' ? 'text-red-600' : 'text-blue-600'
                        }`}>
                          {check.status === 'OVERDUE' ? `${Math.abs(check.daysUntil)} days overdue` : `In ${check.daysUntil} days`}
                        </p>
                        <p className="text-xs text-gray-500">{check.scheduledDate}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Food Consumption Summary - Only show if health checks aren't displayed */}
            {(!upcomingHealthChecks || upcomingHealthChecks.length === 0) && foodConsumptionSummary && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Utensils className="w-5 h-5 text-orange-500" />
                  Food Consumption
                </h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-orange-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-orange-600">{foodConsumptionSummary.totalQuantity.toFixed(1)}</p>
                    <p className="text-sm text-gray-600">Total Units</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{foodConsumptionSummary.totalRecords}</p>
                    <p className="text-sm text-gray-600">Feeding Records</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Most used: <span className="font-medium">{foodConsumptionSummary.mostUsedFeedType}</span>
                </p>
              </div>
            )}
          </section>
        )}

        {/* Food Consumption - Show if health checks are present (in separate row) */}
        {upcomingHealthChecks && upcomingHealthChecks.length > 0 && foodConsumptionSummary && (
          <section>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Utensils className="w-5 h-5 text-orange-500" />
                Food Consumption Summary
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-orange-600">{foodConsumptionSummary.totalQuantity.toFixed(1)}</p>
                  <p className="text-sm text-gray-600">Total Consumed</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{foodConsumptionSummary.totalRecords}</p>
                  <p className="text-sm text-gray-600">Feeding Records</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-lg font-bold text-blue-600">{foodConsumptionSummary.mostUsedFeedType}</p>
                  <p className="text-sm text-gray-600">Most Used Feed</p>
                </div>
              </div>

              {/* Feed Type Distribution */}
              {foodConsumptionSummary.feedTypeDistribution && foodConsumptionSummary.feedTypeDistribution.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Feed Type Breakdown</h4>
                  <div className="space-y-2">
                    {foodConsumptionSummary.feedTypeDistribution.map((feed, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{feed.feedType}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-orange-500 h-2 rounded-full"
                              style={{ width: `${feed.percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-700 w-16 text-right">{feed.quantity.toFixed(1)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Milk Production Chart Section */}
        {milkProductionData && milkProductionData.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Milk className="w-5 h-5 text-blue-500" />
              Milk Production Trend
            </h2>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-xl font-bold text-blue-600">{(milkProductionSummary?.totalMilkProduced || 0).toFixed(1)} L</p>
                  <p className="text-xs text-gray-600">Total Production</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-xl font-bold text-green-600">{(milkProductionSummary?.dailyAverage || 0).toFixed(1)} L</p>
                  <p className="text-xs text-gray-600">Daily Average</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 text-center">
                  <p className="text-xl font-bold text-yellow-600">{(milkProductionSummary?.averageFatPercentage || 0).toFixed(1)}%</p>
                  <p className="text-xs text-gray-600">Avg Fat %</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-xl font-bold text-purple-600">{milkProductionSummary?.totalRecords || 0}</p>
                  <p className="text-xs text-gray-600">Total Records</p>
                </div>
              </div>

              {/* Milk Production Line Chart */}
              <MilkProductionChart
                data={transformedMilkProductionData}
                height={280}
                showHeader={false}
              />
            </div>
          </section>
        )}

        {/* Charts Section - 4 Charts Grid */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Analytics</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Cow Dung Transaction Chart */}
            {transformedCowDungData && transformedCowDungData.length > 0 ? (
              <div className="bg-white rounded-lg shadow-md p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-2">Cow Dung Transactions</h3>
                <CowDungTransactionChart data={transformedCowDungData} height={280} />
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-2">Cow Dung Transactions</h3>
                <div className="flex items-center justify-center h-48 bg-gray-50 rounded">
                  <div className="text-center">
                    <p className="text-gray-600 text-sm">No cow dung transactions for {selectedDateRange?.label || 'selected period'}</p>
                    <p className="text-gray-400 text-xs mt-1">Try selecting a wider date range like "All Time"</p>
                  </div>
                </div>
              </div>
            )}

            {/* Shed Management Chart */}
            {transformedShedData && transformedShedData.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-2">Shed Management</h3>
                <ShedManagementChart data={transformedShedData} height={280} />
              </div>
            )}

            {/* Inventory Chart */}
            {transformedInventoryData && transformedInventoryData.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-2">Inventory Levels</h3>
                <InventoryChart data={transformedInventoryData} height={280} />
              </div>
            )}

            {/* Medicine Inventory Chart */}
            {transformedMedicineData && transformedMedicineData.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-2">Medicine Stock</h3>
                <MedicineInventoryChart data={transformedMedicineData} height={250} />
              </div>
            )}

            {/* Quality Grade Distribution Chart */}
            {qualityGradeDistribution && qualityGradeDistribution.length > 0 ? (
              <div className="bg-white rounded-lg shadow-md p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-2">Quality Grade Distribution</h3>
                <QualityGradeDistributionChart data={qualityGradeDistribution} height={250} />
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-2">Quality Grade Distribution</h3>
                <div className="flex items-center justify-center h-48 bg-gray-50 rounded">
                  <div className="text-center">
                    <p className="text-gray-600 text-sm">No quality grade data available</p>
                    <p className="text-gray-400 text-xs mt-1">(Data will appear after biogas transactions are recorded)</p>
                  </div>
                </div>
              </div>
            )}

          </div>
        </section>

        {/* Empty State Message */}
        {(!cowDungTransactionData || cowDungTransactionData.length === 0) &&
          (!shedData || shedData.length === 0) &&
          (!inventoryData || inventoryData.length === 0) &&
          (!medicineData || medicineData.length === 0) && (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <p className="text-gray-600">No detailed analytics data available yet</p>
            </div>
          )}
      </div>
    </div>
  );
}
